import store from '@/core/store'
import { addContacts, addContact, editContact, removeContact } from '@/core/store/Slices/ContactsSlice'
import commonsService from '@/services/commons.service'
import contactslabelsService from '../contactslabels/contactslabels.service'
import sessionService from '../user/session/session.service'
import { getUserContacts, addNewContact, editExistingContact, removeContactAPI, _uploadContactImageForContact } from './contacts.api'
class ContactsService {
  async getAllContacts () {
    const allContacts = await getUserContacts()
    store.dispatch(addContacts(allContacts))
    store.commit('CONTACT_LIST:UPDATED', allContacts)
    
    return allContacts
  }

  getAllStoredContacts () {
    return store.getState().contacts.contacts
  }

  getContactsNumber () {
    const contacts = this.getAllStoredContacts()
    return Object.values(contacts).length
  }

  switchToAllContacts () {
    contactslabelsService.switchToContactsBelongingToLabel('Contacts')
    store.commit('CONTACT_LIST:UPDATED', Object.values(this.getContactsForCurrentFilter()))
  }

  switchToRecentlyContactedContacts () {
    contactslabelsService.switchToContactsBelongingToLabel('Recently contacted')
    store.commit('CONTACT_LIST:UPDATED', this.getRecentlyContactedContacts())
  }

  getRecentlyContactedContacts () {
    const contacts = Object.values(this.getAllStoredContacts())
    return contacts.filter(c => c.recentlyContacted)
  }

  getRecentlyContactedContactsNumber () {
    return this.getRecentlyContactedContacts().length
  }

  switchToFrequentlyContactedContacts () {
    contactslabelsService.switchToContactsBelongingToLabel('Frequently contacted')
    store.commit('CONTACT_LIST:UPDATED', this.getFrequentlyContactedContacts())
  }

  getFrequentlyContactedContacts () {
    const contacts = Object.values(this.getAllStoredContacts())
    return contacts.filter(c => c.frequentlyContacted)
  }

  getFrequentlyContactedContactsNumber () {
    return this.getFrequentlyContactedContacts().length
  }

  async addContact (newContact) {
    let contact = await addNewContact(newContact)
    store.dispatch(addContact(contact))
    if (newContact.selectedLabels) {
      contact = await contactslabelsService
        .attachLabelsToContact(contact, newContact.selectedLabels)
    }
    if (newContact.profileImageData) {
      contact = await this.uploadContactImageForContact(contact, newContact.profileImageData)
    }
    store.dispatch(editContact(contact))
    store.commit('CONTACT_LIST:FOCUS', contact)
    store.commit('CONTACT_LIST:UPDATE', this.getContactsForCurrentFilter())
    return contact
  }

  async editContact (contact, skipAPIRequest = false) {
    store.dispatch(editContact(contact))
    if (skipAPIRequest) {
      return contact
    }
    if (contact.profileImageData) {
      contact = await this.uploadContactImageForContact(contact, contact.profileImageData)
    }
    if (contact.selectedLabels) {
      contact = await contactslabelsService
        .attachLabelsToContact(contact, contact.selectedLabels)
    }
    editExistingContact(contact)
    store.commit('CONTACT_LIST:UPDATE', this.getContactsForCurrentFilter())
    return contact
  }

  removeContact (contact) {
    const contactToDelete = Object.values(store.getState().contacts.contacts).filter((_contact) => _contact.id === contact.id)
    store.dispatch(removeContact(contactToDelete[0]))
    store.commit('CONTACT_LIST:UPDATED', Object.values(this.getContactsForCurrentFilter()))
    return removeContactAPI(contactToDelete[0])
  }

  async uploadContactImageForContact (contact, image) {
    const response = await _uploadContactImageForContact(contact, image)
    return response
  }

  getCurrentFilterApplied () {
    return store.getState().contactlabels.currentActiveLabelFilter
  }

  getContactsForLabel (label) {
    const filter = {
      type: label.id ? 'label' : 'group',
      value: label.composedid || label,
      name: label.name || label
      
    }
    return this.getContactsForFilter(filter)
  }

  updateStoredContacts (updatedContacts) {
    updatedContacts.forEach((contact) => {
      store.dispatch(editContact(contact))
    })
    store.commit('CONTACT_LIST:UPDATED', this.getContactsForCurrentFilter())
  }

  getContactsForFilter (filter) {
    const filteredContacts = Object.values(this.getAllStoredContacts()).filter((contact) => {
      if (filter.type === 'label') {
        return !!contact.contactlabels?.find(label => label.composedid === filter.value)
      }
      // WIP: Group filters will be applied here
      return true
    })
    return filteredContacts
  }

  getContactsForCurrentFilter () {
    const filter = this.getCurrentFilterApplied()
    return this.getContactsForFilter(filter)
  }

  getContactByContactEntity (contact) {
    return store.getState().contacts.contacts[contact.id]
  }

  getListOfAllStoredContacts () {
    return Object.values(this.getAllStoredContacts())
  }

  findContactByEmail (email) {
    const allContacts = this.getListOfAllStoredContacts()
    const filteredContacts = allContacts.filter((contact) => {
      return contact?.emails && contact.emails.filter(
        mail => mail.toLowerCase() === email.toLowerCase()
      ).length > 0
    })
    return filteredContacts.length > 0 ? filteredContacts[0] : undefined
  }

  getPersonalInfoIfMailComesFromCustomer (email) {
    if (email?.split('@')[1] === sessionService.getMe().me.defaultdomain) {
      return {
        fromEmailOnlyName: sessionService.getMe().me.fullname,
        firstname: sessionService.getMe().me.fullname,
        contactimageuri: sessionService.getMe().me.profilePictureUrl,
        itsMe: true,
      }
    }
    return undefined
  }

  findAllContactsMatching (toMatch) {
    const allContacts = this.getListOfAllStoredContacts()
    return allContacts.filter((contact) => {
      return (
        contact?.emails.length > 0 &&
        (
          contact.emails.filter(
            mail => mail.toLowerCase().includes(toMatch.toLowerCase())
          ).length > 0 ||
          contact.firstname?.toLowerCase().includes(toMatch.toLowerCase()) ||
          contact.lastname?.toLowerCase().includes(toMatch.toLowerCase())
        )
      )
    })
  }

  searchAllContacts (toMatch) {
    const q = toMatch.toLowerCase()
    return this.getListOfAllStoredContacts().filter((contact) => {
      if (!contact.emails?.length) return false
      return (
        contact.firstname?.toLowerCase().includes(q) ||
        contact.lastname?.toLowerCase().includes(q) ||
        contact.emails.some((email) => typeof email === 'string' && email.toLowerCase().includes(q))
      )
    })
  }

  /* DATA PRESENTATION */
  buildContactFullname (contact) {
    return (contact.firstname || contact.lastname) ? (contact.firstname || '') + ' ' + (contact.lastname || '') : undefined
  }

  travelToContactViewAndFocusContact (contact) {
    if (contact.itsMe) {
      return commonsService.goToPage('/settings')
    }
    commonsService.goToPage('/contacts')
    setTimeout(() => {
      store.commit('CONTACT_VIEW:UPDATED', contact)
      store.commit('CONTACT_LIST:FOCUS', contact)
    }, 150)
  }
}

export default new ContactsService()
